<script setup lang="ts">
import { ChevronDown, Search, X } from "lucide-vue-next";

type ComboboxOption = {
  label: string;
  value: string;
};

const props = defineProps<{
  modelValue?: string;
  searchText?: string;
  options: ComboboxOption[];
  placeholder?: string;
  notFoundText?: string;
  clearInvalidOnClose?: boolean;
  dropdownMinHeight?: string;
  dropdownMaxHeight?: string;
  inputClass?: string;
  activeClass?: string;
  contentClass?: string;
  showChevron?: boolean;
  disabled?: boolean;
  mobileActionText?: string;
  clearButtonClass?: string;
  clearIconClass?: string;
  error?: string | boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: string];
  "update:searchText": [value: string];
}>();

const boxRef = useTemplateRef<HTMLElement>("boxRef");
const desktopPanelRef = useTemplateRef<HTMLElement>("desktopPanelRef");
const mobilePanelRef = useTemplateRef<HTMLElement>("mobilePanelRef");
const desktopInputRef = useTemplateRef<HTMLInputElement>("desktopInputRef");
const mobileInputRef = useTemplateRef<HTMLInputElement>("mobileInputRef");

let previousBodyOverflow = "";

const isOpen = ref(false);
const isMobile = ref(false);
// Empty-string options (for example “All cities” / “All services”) are semantically valid choices, but an empty model is also our untouched state
// Keep that distinction locally so “All” is only shown as selected after the user actually chooses it, never on first render
const explicitEmptySelection = ref(false);
let focusDropdownInputOnOpen = false;

const shouldClearInvalidOnClose = computed(() => {
  return props.clearInvalidOnClose ?? true;
});

const dropdownEmptyHeightClass = computed(() => {
  return props.dropdownMinHeight || "min-h-[112px]";
});

const dropdownMaxHeightClass = computed(() => {
  return props.dropdownMaxHeight || "max-h-[260px]";
});

const dropdownListHeightClasses = computed(() => {
  return [
    filteredOptions.value.length ? "" : dropdownEmptyHeightClass.value,
    dropdownMaxHeightClass.value,
  ];
});

const emptyOption = computed(() =>
  props.options.find(option => option.value === ""),
);

const query = computed({
  get: () =>
    props.searchText ||
    (explicitEmptySelection.value ? emptyOption.value?.label || "" : ""),
  set: value => emit("update:searchText", value),
});

const selectedOption = computed(() => {
  if (explicitEmptySelection.value && !props.modelValue) {
    return emptyOption.value;
  }

  if (!props.modelValue) return undefined;

  return props.options.find(option => option.value === props.modelValue);
});

const hasSelection = computed(
  () => Boolean(props.modelValue) || explicitEmptySelection.value,
);

const filteredOptions = computed(() => {
  const normalizedQuery = query.value.trim().toLowerCase();

  if (!normalizedQuery) return props.options;

  return props.options.filter(option =>
    option.label.toLowerCase().includes(normalizedQuery),
  );
});

function isOptionSelected(option: ComboboxOption) {
  if (option.value === "") {
    return explicitEmptySelection.value && !props.modelValue;
  }

  return props.modelValue === option.value;
}

function updateViewport() {
  if (typeof window === "undefined") return;

  isMobile.value = window.matchMedia("(max-width: 767px)").matches;
}

function lockBodyScroll() {
  if (typeof document === "undefined") return;
  if (!isMobile.value) return;

  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
}

function unlockBodyScroll() {
  if (typeof document === "undefined") return;

  document.body.style.overflow = previousBodyOverflow;
}

function syncSelectedText() {
  if (explicitEmptySelection.value && !props.modelValue) {
    const label = emptyOption.value?.label || "";

    if (label && props.searchText !== label) {
      emit("update:searchText", label);
    }
    return;
  }

  if (!props.modelValue) return;

  const option = props.options.find(item => item.value === props.modelValue);

  if (option && props.searchText !== option.label) {
    emit("update:searchText", option.label);
  }
}

function openDropdown(event?: FocusEvent | MouseEvent) {
  if (props.disabled) return;

  if (hasSelection.value) {
    emit("update:searchText", "");
  } else {
    syncSelectedText();
  }

  focusDropdownInputOnOpen = isMobile.value || !event;
  isOpen.value = true;

  if (isMobile.value) {
    const target = event?.target as HTMLInputElement | null;
    target?.blur();
  }
}

function closeDropdown() {
  isOpen.value = false;

  if (!shouldClearInvalidOnClose.value) return;

  if (!props.modelValue) {
    emit(
      "update:searchText",
      explicitEmptySelection.value ? emptyOption.value?.label || "" : "",
    );
    return;
  }

  if (selectedOption.value) {
    emit("update:searchText", selectedOption.value.label);
  }
}

function selectOption(option: ComboboxOption) {
  const isExplicitEmpty = option.value === "";

  explicitEmptySelection.value = isExplicitEmpty;
  emit("update:modelValue", option.value);
  emit("update:searchText", option.label);

  if (!isMobile.value) {
    isOpen.value = false;
  }
}

function clearSelection() {
  explicitEmptySelection.value = false;
  emit("update:modelValue", "");
  emit("update:searchText", "");
  isOpen.value = false;
}

function handleInput() {
  if (props.disabled) return;

  explicitEmptySelection.value = false;
  emit("update:modelValue", "");
  isOpen.value = true;
}

function handleEscPress(event: KeyboardEvent) {
  if (event.key === "Escape") {
    closeDropdown();
  }
}

function handlePointerDown(event: PointerEvent) {
  if (!isOpen.value) return;

  const target = event.target as Node;

  if (boxRef.value?.contains(target)) return;
  if (desktopPanelRef.value?.contains(target)) return;
  if (mobilePanelRef.value?.contains(target)) return;

  closeDropdown();
}

watch(
  () => [props.modelValue, props.searchText] as const,
  ([modelValue, searchText]) => {
    if (modelValue) {
      explicitEmptySelection.value = false;
      return;
    }

    if (!isOpen.value && searchText !== emptyOption.value?.label) {
      explicitEmptySelection.value = false;
    }
  },
);

watch(
  isOpen,
  value => {
    if (!value) {
      focusDropdownInputOnOpen = false;
      unlockBodyScroll();
      return;
    }

    lockBodyScroll();

    if (!focusDropdownInputOnOpen) return;

    const input = isMobile.value ? mobileInputRef.value : desktopInputRef.value;
    input?.focus();
    focusDropdownInputOnOpen = false;
  },
  { flush: "post" },
);

watch(
  () => [props.modelValue, props.options] as const,
  () => {
    syncSelectedText();
  },
  { immediate: true },
);

onMounted(() => {
  updateViewport();

  window.addEventListener("resize", updateViewport);
  window.addEventListener("keyup", handleEscPress);
  document.addEventListener("pointerdown", handlePointerDown);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateViewport);
  window.removeEventListener("keyup", handleEscPress);
  document.removeEventListener("pointerdown", handlePointerDown);
  unlockBodyScroll();
});

defineExpose({ openDropdown });
</script>

<template>
  <div ref="boxRef" class="relative min-w-0">
    <label
      class="flex min-w-0 items-center gap-3 px-5 py-4 transition duration-300 focus-within:bg-primary-50/60"
      :title="selectedOption?.label || query || placeholder || ''"
      :class="[
        inputClass,
        hasSelection || isOpen ? activeClass || '' : '',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-text',
        error
          ? '!border-app-danger focus-within:!border-app-danger focus-within:!ring-app-danger/10'
          : '',
      ]"
      @click="openDropdown"
    >
      <slot
        name="icon"
        :open="isOpen"
        :selected="hasSelection"
        :active="hasSelection || isOpen"
      >
        <Search
          class="h-5 w-5 shrink-0 transition-colors duration-300"
          :class="hasSelection || isOpen ? 'text-primary' : 'text-[#181818]'"
        />
      </slot>

      <input
        ref="desktopInputRef"
        v-model="query"
        type="text"
        :readonly="isMobile || disabled"
        :disabled="disabled"
        :placeholder="placeholder || 'Search'"
        :aria-invalid="Boolean(error)"
        class="w-full min-w-0 border-0 bg-transparent text-sm text-[#181818] outline-none placeholder:text-[#9CA3AF] sm:text-base"
        @focus="openDropdown"
        @input="handleInput"
      />

      <button
        v-if="!disabled && (hasSelection || query)"
        type="button"
        class="flex size-5 shrink-0 items-center justify-center rounded-full transition duration-300"
        :class="clearButtonClass || 'bg-gray-400 text-white hover:bg-gray-500'"
        @click.stop="clearSelection"
      >
        <X :class="clearIconClass || 'size-3.5'" />
      </button>

      <ChevronDown
        v-else-if="showChevron"
        class="h-4 w-4 shrink-0 text-[#181818] transition-transform duration-300"
        :class="{ 'rotate-180': isOpen }"
      />
    </label>

    <!-- Desktop dropdown -->
    <Transition name="combobox-fade">
      <div
        v-if="isOpen"
        ref="desktopPanelRef"
        class="absolute left-0 right-0 top-[calc(100%+8px)] z-[1200] overflow-hidden rounded-xl border border-app-border bg-white p-0 shadow-xl"
        :class="[contentClass, 'hidden md:block']"
        data-scroll-lock-ignore
        @wheel.stop
        @touchmove.stop
      >
        <ul
          class="app-combobox-scroll flex flex-col divide-y divide-app-border overflow-y-scroll overscroll-contain"
          :class="dropdownListHeightClasses"
          data-scroll-lock-ignore
          @wheel.stop
          @touchmove.stop
        >
          <li v-for="option in filteredOptions" :key="option.value">
            <button
              type="button"
              class="flex min-h-[60px] w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold transition duration-300"
              :class="[
                isOptionSelected(option)
                  ? 'bg-primary-50/70 text-app-text'
                  : 'text-app-text hover:bg-primary-50/70',
              ]"
              @click="selectOption(option)"
            >
              <span
                class="min-w-0 flex-1 whitespace-normal break-words text-left leading-5"
                :title="option.label"
              >
                {{ option.label }}
              </span>

              <span
                class="flex size-5 shrink-0 items-center justify-center rounded-full border transition duration-300"
                :class="
                  isOptionSelected(option)
                    ? 'border-primary bg-primary'
                    : 'border-[#98A2A8] bg-white'
                "
                aria-hidden="true"
              >
                <span
                  v-if="isOptionSelected(option)"
                  class="size-1.5 rounded-full bg-white"
                />
              </span>
            </button>
          </li>

          <li
            v-if="!filteredOptions.length"
            class="flex flex-1 items-center justify-center px-4 py-4 text-center text-sm text-[#6B7280]"
          >
            {{ notFoundText || "Nothing found" }}
          </li>
        </ul>
      </div>
    </Transition>

    <!-- Mobile sheet -->
    <Teleport to="body">
      <Transition name="combobox-backdrop">
        <button
          v-if="isOpen"
          type="button"
          class="fixed inset-0 z-[1000] bg-black/40 md:hidden"
          aria-label="Close combobox"
          @click="closeDropdown"
        />
      </Transition>

      <Transition name="combobox-sheet">
        <div
          v-if="isOpen"
          ref="mobilePanelRef"
          class="fixed inset-x-0 bottom-0 z-[1010] flex max-h-[78dvh] min-h-[420px] flex-col overflow-hidden rounded-t-2xl border border-black/10 bg-white p-3 shadow-2xl md:hidden"
          data-scroll-lock-ignore
          @wheel.stop
          @touchmove.stop
        >
          <div
            class="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-[#D1D5DB]"
          />

          <label
            class="mb-3 flex h-11 shrink-0 items-center gap-3 rounded-xl border border-app-border bg-white px-3 transition duration-300 focus-within:border-primary"
          >
            <Search class="h-5 w-5 shrink-0 text-[#6B7280]" />

            <input
              ref="mobileInputRef"
              v-model="query"
              type="text"
              :placeholder="placeholder || 'Search'"
              class="w-full min-w-0 border-0 bg-transparent text-base text-[#181818] outline-none placeholder:text-[#9CA3AF]"
              @input="handleInput"
            />

            <button
              v-if="hasSelection || query"
              type="button"
              class="flex size-7 shrink-0 items-center justify-center rounded-full bg-gray-400 text-white"
              @click.stop="clearSelection"
            >
              <X :class="clearIconClass || 'size-4'" />
            </button>
          </label>

          <ul
            class="app-combobox-scroll flex min-h-0 flex-1 flex-col divide-y divide-app-border overflow-y-auto overscroll-contain"
            data-scroll-lock-ignore
            @wheel.stop
            @touchmove.stop
          >
            <li v-for="option in filteredOptions" :key="option.value">
              <button
                type="button"
                class="flex min-h-[60px] w-full items-center justify-between gap-3 px-3 py-3 text-left text-base font-semibold transition duration-300"
                :class="
                  isOptionSelected(option)
                    ? 'bg-primary-50/70 text-app-text'
                    : 'text-app-text active:bg-primary-50/70'
                "
                @click="selectOption(option)"
              >
                <span
                  class="min-w-0 flex-1 whitespace-normal break-words text-left leading-6"
                  :title="option.label"
                >
                  {{ option.label }}
                </span>

                <span
                  class="flex size-5 shrink-0 items-center justify-center rounded-full border transition duration-300"
                  :class="
                    isOptionSelected(option)
                      ? 'border-primary bg-primary'
                      : 'border-[#98A2A8] bg-white'
                  "
                  aria-hidden="true"
                >
                  <span
                    v-if="isOptionSelected(option)"
                    class="size-1.5 rounded-full bg-white"
                  />
                </span>
              </button>
            </li>

            <li
              v-if="!filteredOptions.length"
              class="flex flex-1 items-center justify-center px-3 py-8 text-center text-sm text-[#6B7280]"
            >
              {{ notFoundText || "Nothing found" }}
            </li>
          </ul>

          <button
            type="button"
            class="mt-3 flex h-12 shrink-0 items-center justify-center rounded-lg bg-primary text-base font-bold text-white transition duration-300 hover:bg-primary-400"
            @click="closeDropdown"
          >
            {{ mobileActionText || "Show results" }}
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.combobox-fade-enter-active,
.combobox-fade-leave-active {
  transition:
    opacity 140ms ease,
    transform 140ms ease;
}

.combobox-fade-enter-from,
.combobox-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

.combobox-backdrop-enter-active,
.combobox-backdrop-leave-active {
  transition: opacity 160ms ease;
}

.combobox-backdrop-enter-from,
.combobox-backdrop-leave-to {
  opacity: 0;
}

.combobox-sheet-enter-active,
.combobox-sheet-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.combobox-sheet-enter-from,
.combobox-sheet-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
.app-combobox-scroll {
  scrollbar-width: thin;
  scrollbar-color: #aeb8bf #f4f6f7;
  scrollbar-gutter: stable;
}

.app-combobox-scroll::-webkit-scrollbar {
  width: 9px;
  height: 8px;
}

.app-combobox-scroll::-webkit-scrollbar-thumb {
  border-radius: 9999px;
  border: 2px solid #f4f6f7;
  background: #aeb8bf;
}

.app-combobox-scroll::-webkit-scrollbar-track {
  background: #f4f6f7;
}
</style>
